/* bfc-plan.js — Best Friend Club, the ten-step plan questionnaire, the veterinary-diet branch and the results screen.
   Vanilla only, no dependencies, no build step.

   NO RULES LIVE IN THIS FILE. Every calculation goes through bfc-engine.js (window.BFCEngine), which is a
   byte-for-byte copy of repo/packages/engine/src/engine.js, fed by the JSON config the section prints from
   snippets/bfc-engine-config.liquid. BFC's ten worked profiles run against that engine in
   repo/packages/engine/test. Change a rule in repo/packages/engine/config, then run repo/scripts/sync-theme.js. */
(function () {
  'use strict';

  /* ============================ ENGINE ============================ */

  var ENGINE = null, ENGINE_ERROR = null;
  function engine() {
    if (ENGINE || ENGINE_ERROR) return ENGINE;
    try {
      var el = document.querySelector('[data-bfc-engine-config]');
      if (!window.BFCEngine) throw new Error('bfc-engine.js did not load');
      if (!el) throw new Error('the engine config is missing from the page');
      ENGINE = window.BFCEngine.createEngine(JSON.parse(el.textContent));
    } catch (e) {
      ENGINE_ERROR = e;
      console.error('[bfc-plan]', e);
    }
    return ENGINE;
  }

  var VET_DIETS = ['Renal / kidney', 'Gastrointestinal / digestive', 'Hypoallergenic / food sensitivity',
    'Low fat / pancreatitis', 'Urinary', 'Weight management', 'Liver / hepatic', 'Diabetes',
    'Joint / mobility', 'Other', 'Not sure'];

  /* display: nearest 5g per day — full precision kept in the engine */
  function g5(n) { return Math.round(n / 5) * 5; }
  function money(pence) { return '£' + (pence / 100).toFixed(2); }

  /* ============================ THE STEPS ============================ */

  /* 2026-10-20 (Dom, after a UX review): step 1 asks only for the dog's name. Name and email are asked on the
     results screen, and only if the customer wants the plan emailed ("Send me <dog>'s plan"). */
  var STEPS = [
    { id: 'who',      n: 1,  legend: 'Who are we building the plan for?',
      note: 'We save your answers as you go, so you can come back to this.' },
    { id: 'breed',    n: 2,  legend: 'What breed is {dog}?',
      note: 'Breed only fine-tunes the portion by up to 5%. We never guess weight from breed.' },
    { id: 'age',      n: 3,  legend: 'How old is {dog}?',
      note: 'We make adult food, so we need to know {dog} is over a year old.' },
    { id: 'weight',   n: 4,  legend: 'What does {dog} weigh?',
      note: 'A recent weight, not an estimate. Your vet will weigh {dog} for free.' },
    { id: 'neutered', n: 5,  legend: 'Is {dog} neutered?', note: 'It changes how much energy {dog} burns.' },
    { id: 'activity', n: 6,  legend: 'How active is {dog}?', note: 'Think of a normal week, not a good one.' },
    { id: 'goal',     n: 7,  legend: 'Does {dog} need to change weight?',
      note: 'If you are not sure, your vet can body-score {dog} in a minute.' },
    { id: 'vet',      n: 8,  legend: 'Does {dog} need a veterinary diet?',
      note: 'A prescription diet your vet has put {dog} on, not a preference.' },
    { id: 'recipe',   n: 9,  legend: 'Would {dog} prefer meat or fish?', note: 'You can switch recipe at any time.' },
    { id: 'review',   n: 10, legend: 'Does this look right?', note: 'Change anything before we work out the plan.' }
  ];

  var CHOICES = {
    neutered: [
      { v: 'yes',    l: 'Yes' },
      { v: 'no',     l: 'No' },
      { v: 'unsure', l: 'Not sure' }
    ],
    activity: [
      { v: 'couch_potato', l: 'A bit of a couch potato', d: 'A gentle stroll, then the sofa. Under an hour on their feet.' },
      { v: 'typical',      l: 'Typical',                 d: 'One or two proper walks a day, a bit of a run about.' },
      { v: 'active',       l: 'Active',                  d: 'Hours out, working, running, or genuinely never still.' }
    ],
    goal: [
      { v: 'gain',        l: 'Could gain a little', d: 'You can feel the ribs easily and see a tuck from above.' },
      { v: 'about_right', l: 'About right',         d: 'Ribs there under a light cover, a waist you can see.' },
      { v: 'lose',        l: 'Could lose a little', d: 'Ribs are hard to feel and the waist has gone.' }
    ],
    vet: [
      { v: 'no',     l: 'No' },
      { v: 'yes',    l: 'Yes' },
      { v: 'unsure', l: 'Not sure' }
    ],
    recipe: [
      { v: 'chicken', l: 'Chicken', d: 'British chicken, 369 kcal per 100g.' },
      { v: 'salmon',  l: 'Salmon',  d: 'Salmon with omega 3 and 6, 368 kcal per 100g.' },
      { v: 'none',    l: 'No preference', d: 'We will start {dog} on salmon. Switch whenever you like.' }
    ]
  };

  /* ============================ CONTROLLER ============================ */

  function init(root) {
    var stage = root.querySelector('[data-stage]');
    var railFill = root.querySelector('[data-rail-fill]');
    var railText = root.querySelector('[data-rail-text]');
    var backBtn = root.querySelector('[data-back]');
    var live = root.querySelector('[data-live]');
    var title = root.querySelector('[data-title]');
    var titleDefault = title ? title.textContent : '';
    var card = root.querySelector('.bfc-plan__card');
    if (!stage) return;

    var opt = function (k, d) { var v = root.getAttribute('data-' + k); return v === null || v === '' ? d : v; };
    var TRIAL_PENCE = Math.round(parseFloat(opt('trial-price', '7.50')) * 100);
    var TRIAL_KG = parseFloat(opt('trial-kg', '2'));
    var FIRST_SHIP_DAYS = parseInt(opt('ship-days', '3'), 10);
    var TRIAL_CUTOFF_DAYS = parseInt(opt('trial-cutoff-days', '3'), 10);
    var BAGS_SRC = {
      chicken: { 2: opt('bag-chicken-2', ''), 10: opt('bag-chicken-10', '') },
      salmon:  { 2: opt('bag-salmon-2', ''),  10: opt('bag-salmon-10', '') }
    };
    var STORE_KEY = 'bfc-plan-v1';
    /* Revision 2 (Defaults 6): lifetime campaign codes. The list is the shop metafield bfc.campaign_codes (the service
       reads the same list). A listed code the customer brought in by link or typed wins; with none, DEFAULT_CODE (WEB10)
       applies. The percentage comes off the plan price and every renewal, never off the trial bag. */
    var CODES = opt('codes', '').split(',').map(function (c) { return c.trim().toUpperCase(); }).filter(Boolean);
    var DEFAULT_CODE = opt('default-code', 'WEB10').trim().toUpperCase();
    var OFFER_TRIAL = opt('offer-trial', '1') !== '0';

    var a = {};                      /* the answers */
    var idx = 0;                     /* index into STEPS */
    var screen = 'step';             /* step | stop-age | stop-vet | result | confirm-plan | confirm-trial */
    var editing = false;             /* came from the review step, so Continue returns there */

    try { var saved = localStorage.getItem(STORE_KEY); if (saved) a = JSON.parse(saved) || {}; } catch (e) { a = {}; }
    /* "Add another dog" from the account page: a new dog, same owner. Keep who they are, forget the last dog. */
    if (/[?&]new=1\b/.test(location.search)) {
      a = { firstName: a.firstName, email: a.email, marketing: a.marketing };
      try { history.replaceState(null, '', location.pathname + location.search.replace(/([?&])new=1(&|$)/, '$1').replace(/[?&]$/, '') + location.hash); } catch (e) {}
    }
    a = sanitize(a);
    try { if (Object.keys(a).length) localStorage.setItem(STORE_KEY, JSON.stringify(a)); } catch (e) {}
    function save() { try { localStorage.setItem(STORE_KEY, JSON.stringify(a)); } catch (e) {} }

    /* Saved answers come from the visitor's browser, so anything not on the lists is dropped and that step
       is asked again, rather than reaching the engine and failing on the results screen. */
    function sanitize(s) {
      var out = s && typeof s === 'object' && !Array.isArray(s) ? s : {};
      Object.keys(CHOICES).forEach(function (k) {
        if (out[k] != null && !CHOICES[k].some(function (o) { return o.v === out[k]; })) delete out[k];
      });
      if (['purebred', 'cross', 'unknown'].indexOf(out.breedType) < 0) { delete out.breedType; delete out.breeds; }
      var names = engine() ? engine().breedNames() : [];
      if (!Array.isArray(out.breeds)) out.breeds = [];
      out.breeds = out.breeds.filter(function (b) { return names.indexOf(b) >= 0; }).slice(0, out.breedType === 'purebred' ? 1 : 3);
      if (out.weightUnit !== 'lb') out.weightUnit = 'kg';
      if (VET_DIETS.indexOf(out.vetType) < 0) out.vetType = '';
      return out;
    }

    function dog() { return (a.dogName || '').trim() || 'your dog'; }
    function fill(s) { return s.replace(/\{dog\}/g, esc(dog())); }
    function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

    /* ---- validity per step, which is also what gates the Continue button ---- */
    function valid(id) {
      switch (id) {
        case 'who':      return !!(a.dogName || '').trim();
        case 'breed':    return a.breedType === 'unknown' ||
                                (a.breedType === 'purebred' && !!a.breeds && a.breeds.length === 1) ||
                                (a.breedType === 'cross' && !!a.breeds && a.breeds.length >= 1);
        case 'age':      return ageMonths() !== null;
        case 'weight':   return weightKg() > 0 && (!engine() || weightKg() <= engine().config.eligibility.maxWeightKg);
        case 'neutered': return !!a.neutered;
        case 'activity': return !!a.activity;
        case 'goal':     return !!a.goal;
        case 'vet':      return !!a.vet && (a.vet !== 'yes' || !!a.vetType);
        case 'recipe':   return !!a.recipe;
        case 'review':   return true;
      }
      return false;
    }
    function ageMonths() {
      var y = parseInt(a.ageYears, 10), m = parseInt(a.ageMonths, 10);
      if (isNaN(y) && isNaN(m)) return null;
      return (isNaN(y) ? 0 : y) * 12 + (isNaN(m) ? 0 : m);
    }
    function weightKg() {
      var w = parseFloat(a.weight);
      if (!(w > 0)) return 0;
      return a.weightUnit === 'lb' ? w * 0.45359237 : w;
    }
    /* Revision 2 item 41: "No preference" starts on salmon. */
    function recipeKey() { return a.recipe === 'chicken' ? 'chicken' : 'salmon'; }
    function breedList() { return a.breedType === 'unknown' ? [] : (a.breeds || []); }

    /* the answers in the shape the engine takes */
    function engineInput() {
      return { weightKg: weightKg(), neutered: a.neutered, activity: a.activity, goal: a.goal,
               breeds: breedList(), recipe: recipeKey() };
    }

    /* ---- rendering ---- */
    function render(focusChoice) {
      var html;
      if (screen === 'stop-age') html = viewStopAge();
      else if (screen === 'stop-vet') html = viewStopVet();
      else if (screen === 'result') html = viewResult();
      else if (screen === 'confirm-plan') html = viewConfirmPlan();
      else if (screen === 'confirm-trial') html = viewConfirmTrial();
      else html = viewStep(STEPS[idx]);

      stage.innerHTML = html;
      stage.querySelectorAll('.bfc-q-anim').forEach(function (el, i) {
        el.style.setProperty('--i', Math.min(i, 5));
        requestAnimationFrame(function () { el.classList.add('is-in'); });
      });
      wire();
      rail();
      var h = stage.querySelector('h2, h3');
      var back = null;
      if (focusChoice) {
        var parts = focusChoice.split('|');
        back = stage.querySelector('[data-choice="' + parts[0] + '"][data-value="' + parts[1].replace(/"/g, '\\"') + '"]');
      }
      if (back) back.focus({ preventScroll: true });
      else if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
      if (live) live.textContent = h ? h.textContent : '';
      var anchor = card || root;
      var top = anchor.getBoundingClientRect().top + window.pageYOffset - 96;
      if (window.pageYOffset > top) window.scrollTo({ top: top, behavior: 'smooth' });
    }

    function rail() {
      var on = screen === 'step';
      /* the results screen needs the full width, so the reassurance aside is dropped there */
      root.classList.toggle('bfc-plan--wide', screen === 'result');
      if (railFill) railFill.style.width = (on ? ((idx + 1) / STEPS.length) * 100 : 100) + '%';
      if (railText) railText.textContent = on ? ('Step ' + STEPS[idx].n + ' of ' + STEPS.length)
        : (screen === 'result' ? 'Your plan' : (screen.indexOf('confirm') === 0 ? 'Before you pay' : 'One moment'));
      if (backBtn) backBtn.hidden = !(screen === 'step' ? idx > 0 : true);
      if (title) {
        var named = (a.dogName || '').trim();
        title.textContent = screen.indexOf('confirm') === 0 ? (named ? 'Check ' + named + '\u2019s plan' : 'Check the plan')
          : screen === 'result' && named ? (named + '\u2019s plan')
          : (named ? ('Building ' + named + '\u2019s plan') : titleDefault);
      }
    }

    function head(step) {
      return '<p class="bfc-q__note bfc-q-anim">' + fill(step.note) + '</p>' +
             '<h2 class="bfc-q__legend bfc-q-anim">' + fill(step.legend) + '</h2>';
    }

    function radios(name, list, current) {
      return '<div class="bfc-q__opts bfc-q-anim" role="radiogroup" aria-label="' + esc(name) + '">' +
        list.map(function (o, i) {
          var on = current === o.v;
          /* roving tabindex: one stop for the whole group, arrows move within it (wired in wire()) */
          var tab = on || (current == null && i === 0) ? 0 : -1;
          return '<button type="button" class="bfc-q__opt' + (on ? ' is-on' : '') + '" role="radio" aria-checked="' +
            (on ? 'true' : 'false') + '" tabindex="' + tab + '" data-choice="' + name + '" data-value="' + esc(o.v) + '">' +
            '<span class="bfc-q__tick" aria-hidden="true"></span>' +
            '<span class="bfc-q__optbody"><span class="bfc-q__optl">' + fill(o.l) + '</span>' +
            (o.d ? '<span class="bfc-q__optd">' + fill(o.d) + '</span>' : '') + '</span></button>';
        }).join('') + '</div>';
    }

    function viewStep(step) {
      var body = '';
      if (step.id === 'who') {
        body =
          '<div class="bfc-q__fields bfc-q-anim">' +
            field('dogName', "Your dog's name", 'text', a.dogName, 'off') +
          '</div>';
      } else if (step.id === 'breed') {
        body = radios('breedType', [
          { v: 'purebred', l: 'One breed' },
          { v: 'cross',    l: 'A mix', d: 'Pick up to three. We average them.' },
          { v: 'unknown',  l: 'Not sure', d: 'Perfectly fine. We use a neutral figure.' }
        ], a.breedType);
        if (a.breedType === 'purebred' || a.breedType === 'cross') {
          var max = a.breedType === 'cross' ? 3 : 1;
          body += '<div class="bfc-q__breed bfc-q-anim">' +
            '<div class="bfc-q__chips" data-chips>' + (a.breeds || []).map(function (b) {
              return '<button type="button" class="bfc-q__chip" data-unpick="' + esc(b) + '">' + esc(b) +
                     '<span aria-hidden="true">×</span><span class="bfc-sr">Remove</span></button>'; }).join('') + '</div>' +
            ((a.breeds || []).length >= max ? '' :
              '<div class="bfc-q__search">' +
                '<label class="bfc-sr" for="bfc-breed">Search breeds</label>' +
                '<input id="bfc-breed" type="text" autocomplete="off" data-breed-input placeholder="Start typing a breed">' +
                '<ul class="bfc-q__list" data-breed-list hidden></ul>' +
              '</div>') +
            '<p class="bfc-q__hint">' + (a.breedType === 'cross' ? 'Up to three breeds.' : 'One breed.') + '</p>' +
          '</div>';
        }
      } else if (step.id === 'age') {
        body = '<div class="bfc-q__fields bfc-q__fields--pair bfc-q-anim">' +
          field('ageYears', 'Years', 'number', a.ageYears, 'off', { min: 0, max: 30 }) +
          field('ageMonths', 'Months', 'number', a.ageMonths, 'off', { min: 0, max: 11 }) +
        '</div>';
      } else if (step.id === 'weight') {
        body = '<div class="bfc-q__weight bfc-q-anim">' +
          '<div class="bfc-q__fields">' + field('weight', 'Weight', 'number', a.weight, 'off', { min: 0.5, max: 120, step: 0.1 }) + '</div>' +
          '<div class="bfc-q__unit" role="radiogroup" aria-label="Unit">' +
            ['kg', 'lb'].map(function (u) {
              var on = (a.weightUnit || 'kg') === u;
              return '<button type="button" class="bfc-q__unitb' + (on ? ' is-on' : '') + '" role="radio" aria-checked="' +
                (on ? 'true' : 'false') + '" data-choice="weightUnit" data-value="' + u + '">' + u + '</button>'; }).join('') +
          '</div></div>';
      } else if (step.id === 'vet') {
        body = radios('vet', CHOICES.vet, a.vet);
        if (a.vet === 'unsure') {
          body += '<div class="bfc-q__aside bfc-q-anim"><strong>We are not a veterinary diet.</strong> ' +
            'If ' + esc(dog()) + ' might need one, check with your vet before you change food. You can carry on for now.</div>';
        }
        if (a.vet === 'yes') {
          body += '<div class="bfc-q__aside bfc-q-anim"><strong>Which type?</strong></div>' +
            radios('vetType', VET_DIETS.map(function (d) { return { v: d, l: d }; }), a.vetType);
        }
      } else if (step.id === 'review') {
        body = viewReview();
      } else {
        body = radios(step.id, CHOICES[step.id], a[step.id]);
      }

      var last = step.id === 'review';
      var ok = valid(step.id);
      return head(step) + body +
        '<div class="bfc-q__actions bfc-q-anim">' +
          '<button type="button" class="bfc-btn bfc-btn--lg" data-next' + (ok ? '' : ' disabled aria-describedby="bfc-q-hint"') + '>' +
            (last ? 'See the plan' : (editing ? 'Save and go back' : 'Continue')) +
          '</button>' +
          (ok ? '' : '<span class="bfc-q__gate" id="bfc-q-hint" data-gate>' + esc(gateHint(step.id)) + '</span>') +
        '</div>';
    }

    /* why Continue is off — a disabled button with no explanation is a dead end */
    function gateHint(id) {
      switch (id) {
        case 'who':    return 'We need your dog’s name.';
        case 'breed':  return a.breedType ? 'Pick a breed to carry on.' : 'Pick one of the three.';
        case 'age':    return 'Give an age, in years or months.';
        case 'weight': return engine() && weightKg() > engine().config.eligibility.maxWeightKg
                         ? 'That is more than we can plan for. Check the weight and the unit.' : 'A recent weight, in kg or lb.';
        case 'vet':    return a.vet === 'yes' ? 'Pick the type of diet.' : 'Pick an answer to carry on.';
        default:       return 'Pick an answer to carry on.';
      }
    }

    /* arrow keys move inside a radiogroup, as a radiogroup is expected to */
    function arrowKeys() {
      stage.querySelectorAll('[role="radiogroup"]').forEach(function (grp) {
        var opts = Array.prototype.slice.call(grp.querySelectorAll('[role="radio"]'));
        grp.addEventListener('keydown', function (e) {
          var i = opts.indexOf(document.activeElement);
          if (i < 0) return;
          var step = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1
                   : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0;
          if (!step) return;
          e.preventDefault();
          opts[(i + step + opts.length) % opts.length].click();
        });
      });
    }

    function field(name, label, type, value, ac, num) {
      num = num || {};
      return '<p class="bfc-q__field"><label for="bfc-f-' + name + '">' + esc(label) + '</label>' +
        '<input id="bfc-f-' + name + '" type="' + type + '" data-field="' + name + '" value="' + esc(value || '') +
        '" autocomplete="' + ac + '"' +
        (num.min != null ? ' min="' + num.min + '"' : '') + (num.max != null ? ' max="' + num.max + '"' : '') +
        (num.step != null ? ' step="' + num.step + '"' : (type === 'number' ? ' step="1"' : '')) +
        (type === 'number' ? ' inputmode="decimal"' : '') + '></p>';
    }

    function label(kind, v) {
      var list = CHOICES[kind];
      if (!list) return v || '';
      for (var i = 0; i < list.length; i++) if (list[i].v === v) return list[i].l;
      return v || '';
    }

    function viewReview() {
      var breeds = a.breedType === 'unknown' ? 'Not sure' : (a.breeds || []).join(', ');
      var m = ageMonths() || 0;
      var age = Math.floor(m / 12) + ' yr' + (m % 12 ? ' ' + (m % 12) + ' mo' : '');
      var rows = [
        ['who', 'Dog', esc(dog())],
        ['breed', 'Breed', esc(breeds)],
        ['age', 'Age', esc(age)],
        ['weight', 'Weight', esc(a.weight || '') + ' ' + esc(a.weightUnit || 'kg')],
        ['neutered', 'Neutered', esc(label('neutered', a.neutered))],
        ['activity', 'Activity', esc(label('activity', a.activity))],
        ['goal', 'Weight goal', esc(label('goal', a.goal))],
        ['vet', 'Veterinary diet', esc(label('vet', a.vet))],
        ['recipe', 'Recipe', esc(label('recipe', a.recipe))]
      ];
      return '<dl class="bfc-q__review bfc-q-anim">' + rows.map(function (r) {
        return '<div class="bfc-q__rrow"><dt>' + r[1] + '</dt><dd>' + r[2] +
          '<button type="button" class="bfc-q__edit" data-edit="' + r[0] + '">Edit<span class="bfc-sr"> ' + r[1] + '</span></button></dd></div>';
      }).join('') + '</dl>';
    }

    function viewStopAge() {
      return '<p class="bfc-q__note bfc-q-anim">A quick stop</p>' +
        '<h2 class="bfc-q__legend bfc-q-anim">' + esc(dog()) + ' is still a puppy</h2>' +
        '<div class="bfc-q__aside bfc-q-anim">We only make adult food at the moment, from twelve months. ' +
        'Puppies need more energy and a different mineral balance than our recipes give, so we would rather not sell you the wrong thing.</div>' +
        waitlistBox('Email me when there is a puppy recipe.') +
        '<div class="bfc-q__actions bfc-q-anim">' +
          waitlistAction() +
          '<button type="button" class="bfc-q__back2" data-edit="age">Change the age</button>' +
        '</div>';
    }

    function viewStopVet() {
      return '<p class="bfc-q__note bfc-q-anim">A quick stop</p>' +
        '<h2 class="bfc-q__legend bfc-q-anim">We do not make veterinary diets yet</h2>' +
        '<div class="bfc-q__aside bfc-q-anim">A prescription diet does a job our food is not formulated to do, ' +
        'so we will not put ' + esc(dog()) + ' on a plan while the vet has them on one. ' +
        'We plan to expand the range.</div>' +
        waitlistBox('Email me if we add something suitable for ' + esc(dog()) + '.') +
        '<div class="bfc-q__actions bfc-q-anim">' +
          waitlistAction() +
          '<button type="button" class="bfc-q__back2" data-edit="vet">That was not right</button>' +
        '</div>';
    }

    /* Dates are UK calendar dates, the same day the service counts from (pricing.js today()), wherever the visitor is */
    function londonToday() {
      try {
        var p = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()).split('-');
        return new Date(+p[0], +p[1] - 1, +p[2], 12, 0, 0, 0);
      } catch (e) { var d = new Date(); d.setHours(12, 0, 0, 0); return d; }
    }
    function addDays(n) { var d = londonToday(); d.setDate(d.getDate() + Math.round(n)); return d; }

    /* Revision 2 item 13: the cycle in plain words, in weeks when it divides evenly */
    function cycleText(days) {
      var d = Math.max(1, Math.round(days));
      if (d % 7 === 0) return d === 7 ? 'every week' : 'every ' + (d / 7) + ' weeks';
      return 'every ' + d + ' days';
    }
    function codeState() {
      var raw = window.BFCCampaign ? String(window.BFCCampaign.get() || '').toUpperCase() : '';
      var src = window.BFCCampaign ? window.BFCCampaign.source() : '';
      var pct = engine() ? engine().config.pricing.attributionDiscountPercent : 0;
      if (raw && CODES.indexOf(raw) >= 0) return { code: raw, pct: pct, source: src || 'stored', entered: raw };
      if (DEFAULT_CODE && CODES.indexOf(DEFAULT_CODE) >= 0) return { code: DEFAULT_CODE, pct: pct, source: 'default', entered: raw || null };
      return { code: null, pct: 0, source: null, entered: raw || null };
    }
    /* the recurring price the customer will actually pay, after the code */
    function planPence(r) { var c = codeState(); return c.pct ? engine().discountedChargePence(r, c.pct).pence : r.cycleChargePence; }
    /* 2026-10-12 (Dom): the silent WEB10 fallback is never named before checkout; the code is shown only when the
       customer brought it in by link, typed it, or took it from the pop-up. */
    function codeNote() {
      var c = codeState();
      if (!c.code) return '';
      return 'Includes ' + c.pct + '% off every delivery' + (c.source === 'default' ? '.' : ' with code ' + esc(c.code) + '.');
    }
    function fmt(d) { return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }); }
    function bagText(bags) {
      var parts = [];
      [10, 2].forEach(function (size) {
        var n = bags[size] || 0;
        if (n) parts.push(n === 1 ? 'one ' + size + 'kg bag' : n + ' × ' + size + 'kg bags');
      });
      var text = parts.join(' + ');
      return text.charAt(0).toUpperCase() + text.slice(1);
    }

    /* the engine cannot run (config missing or invalid): say so plainly, never show a guessed price */
    function viewUnavailable() {
      return '<p class="bfc-q__note bfc-q-anim">Sorry</p>' +
        '<h2 class="bfc-q__legend bfc-q-anim">We could not work out ' + esc(dog()) + '’s plan just now</h2>' +
        '<div class="bfc-q__aside bfc-q-anim">Your answers are saved. Please try again in a minute, or ' +
        '<a href="/pages/contact">get in touch</a> and we will build the plan with you.</div>' +
        '<div class="bfc-q__actions bfc-q-anim"><button type="button" class="bfc-q__back2" data-edit="review">Check my answers</button></div>';
    }

    /* The stop screen a set of answers belongs on, if any. Checked on resume and again before results, so
       saved answers can never walk past the age rule or the vet-diet stop. */
    function gateScreen() {
      if (valid('age') && eligibilityNow('age') === 'puppy') return 'stop-age';
      if (valid('vet') && eligibilityNow('vet') === 'vet_diet') return 'stop-vet';
      return null;
    }

    /* ---- the plan facts both results and the "before you pay" screens show. null if the engine cannot run. ---- */
    /* Revision 3 item 3: on the results screen (opts.offer) the WEB10 fallback is not shown as already taken off.
       The customer is offered it in a pop-up when they start the full plan; a linked or typed code still counts. */
    function planFacts(opts) {
      try {
        if (!engine()) throw ENGINE_ERROR;
        var r = engine().plan(engineInput());
        var c = codeState();
        if (opts && opts.offer && c.source === 'default') c = { code: null, pct: 0, source: null, entered: c.entered };
        return Object.assign({ r: r, recipeName: recipeKey() === 'salmon' ? 'Salmon' : 'Chicken', bagLine: bagText(r.bags),
          pence: c.pct ? engine().discountedChargePence(r, c.pct).pence : r.cycleChargePence, cycle: cycleText(r.cycleDays), code: c }, trialDates(r));
      } catch (e) { console.error('[bfc-plan] plan failed', e); return null; }
    }

    /* Defaults 1 + 3: the trial bag runs out on day D (first delivery + the days it lasts). The first full order is
       charged TRIAL_CUTOFF_DAYS (3) before D, so it arrives at or just before D, never on D with a gap after it.
       It is never charged before the trial has arrived. Cancelling before that charge date stops it.
       The service (repo packages/server, pricing.js buildQuote) uses the same rule, rounded the same way. */
    function trialDates(r) {
      var trialDays = TRIAL_KG * 1000 / r.gramsPerDay;
      var runOut = FIRST_SHIP_DAYS + trialDays;
      var chargeIn = Math.max(runOut - TRIAL_CUTOFF_DAYS, FIRST_SHIP_DAYS);
      return { firstDelivery: addDays(FIRST_SHIP_DAYS), trialDays: trialDays,
        firstFullOrder: addDays(chargeIn), fullDelivery: addDays(chargeIn + FIRST_SHIP_DAYS), cancelBy: addDays(chargeIn) };
    }

    function row(label, value, edit) {
      return '<div class="bfc-pay__row"><dt>' + label + '</dt><dd>' + value +
        (edit ? ' <button type="button" class="bfc-pay__edit" data-edit="' + edit + '">Change<span class="bfc-sr"> ' + label + '</span></button>' : '') + '</dd></div>';
    }

    /* canvas board "1 · Confirm the plan": everything scope §5 says a direct subscriber sees before paying */
    function viewConfirmPlan() {
      var f = planFacts();
      if (!f) return viewUnavailable();
      var r = f.r, name = esc(dog());
      return '<p class="bfc-q__note bfc-q-anim">Before you pay</p>' +
        '<h2 class="bfc-q__legend bfc-q-anim">Check ' + name + '’s plan</h2>' +
        '<p class="bfc-pay__lede bfc-q-anim">Nothing is charged until you confirm on the payment page.</p>' +
        vatBanner() +
        '<p class="bfc-pay__cycle bfc-q-anim">Your delivery cycle: <strong>' + f.cycle + '</strong>.</p>' +
        '<dl class="bfc-pay__list bfc-q-anim">' +
          row('Recipe', f.recipeName + ' · ' + esc((f.bagLine || r.bagsKg + 'kg').toLowerCase()), 'recipe') +
          row('Daily portion', 'About ' + g5(r.gramsPerDay) + 'g a day for ' + name, null) +
          row('Delivery quantity', r.bagsKg + 'kg', null) +
          row('Delivery cycle', cap(f.cycle), null) +
          row('First delivery', fmt(f.firstDelivery), null) +
          row('First payment', 'Today', null) +
          row('Price per delivery', '<strong>' + money(f.pence) + '</strong> <span class="bfc-pay__incl">incl. VAT and delivery</span>', null) +
        '</dl>' +
        (codeNote() ? '<p class="bfc-pay__small bfc-q-anim">' + codeNote() + '</p>' : '') +
        consentBlock('By placing this order, I agree that this starts a recurring Best Friend Club subscription for ' + name +
          ' at <strong>' + money(f.pence) + ' ' + f.cycle + '</strong> for ' + esc(r.bagsKg + 'kg') + ' of ' + f.recipeName.toLowerCase() +
          ', first charged today, with the first delivery expected around ' + fmt(f.firstDelivery) +
          '. I can change recipe, pause, skip, reschedule or cancel online.') +
        payActions('subscribe', '') +
        '<ul class="bfc-pay__notes bfc-q-anim"><li>Payment is taken on Shopify’s secure checkout.</li>' +
          '<li>Change recipe, pause, skip, reschedule or cancel online.</li><li>VAT and UK delivery included.</li></ul>';
    }

    /* canvas board "2 · Trial checkout" + Revision 1 §24's compact summary; Defaults 1: BFC's own consent wording */
    function viewConfirmTrial() {
      var f = planFacts();
      if (!f) return viewUnavailable();
      var r = f.r, name = esc(dog());
      var full = Math.round(TRIAL_PENCE * 2);
      return '<p class="bfc-q__note bfc-q-anim">Trial · before you pay</p>' +
        '<h2 class="bfc-q__legend bfc-q-anim">Your ' + TRIAL_KG + 'kg trial, and what happens after it</h2>' +
        '<div class="bfc-pay__trial bfc-q-anim"><div><strong>' + TRIAL_KG + 'kg ' + f.recipeName.toLowerCase() + ' trial bag</strong>' +
          '<span>Lasts ' + name + ' about ' + Math.round(f.trialDays) + ' days</span></div>' +
          '<p><strong>' + money(TRIAL_PENCE) + '</strong> <s>' + money(full) + '</s><sub class="bfc-pay__rrp">RRP</sub></p></div>' +
        vatBanner() +
        '<p class="bfc-pay__cycle bfc-q-anim">Your delivery cycle: <strong>' + f.cycle + '</strong>.</p>' +
        '<dl class="bfc-pay__list bfc-q-anim">' +
          row('Today', TRIAL_KG + 'kg trial, ' + money(TRIAL_PENCE), null) +
          row('Trial delivery', fmt(f.firstDelivery), null) +
          row('First full order', money(f.pence) + ' for ' + r.bagsKg + 'kg, charged ' + fmt(f.firstFullOrder), null) +
          row('Delivered around', fmt(f.fullDelivery), null) +
          row('Then', money(f.pence) + ' ' + f.cycle, null) +
          row('Cancel before', fmt(f.cancelBy) + ' to stop the first full order', null) +
        '</dl>' +
        (codeNote() ? '<p class="bfc-pay__small bfc-q-anim">' + codeNote() + ' The trial stays at ' + money(TRIAL_PENCE) + '.</p>' : '') +
        '<p class="bfc-pay__small bfc-q-anim">We email you a reminder before that date. Cancel before it and you only ever pay for the trial bag.</p>' +
        consentBlock('By placing this order, I agree that my ' + money(TRIAL_PENCE) + ' trial starts a recurring Best Friend Club subscription for ' + name +
          '. Unless I cancel before <strong>' + fmt(f.cancelBy) + '</strong>, Best Friend Club will automatically charge <strong>' + money(f.pence) +
          '</strong> on <strong>' + fmt(f.firstFullOrder) + '</strong> for ' + esc(r.bagsKg + 'kg') + ' of ' + f.recipeName.toLowerCase() +
          ', with delivery expected around ' + fmt(f.fullDelivery) + '. After that, my plan will renew at ' + money(f.pence) + ' ' + f.cycle +
          '. I can change recipe, pause, skip, reschedule or cancel online.') +
        payActions('trial', '<button type="button" class="bfc-q__back2" data-switch-plan>Start the full plan instead</button>') +
        '<ul class="bfc-pay__notes bfc-q-anim"><li>Payment is taken on Shopify’s secure checkout.</li><li>VAT and UK delivery included.</li></ul>';
    }

    /* Revision 3 item 7: "All prices inclusive of VAT and delivery" stands out on the before-you-pay screens */
    function vatBanner() {
      return '<p class="bfc-pay__vat bfc-q-anim"><svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>' +
        '<span>All prices inclusive of VAT and delivery</span></p>';
    }

    /* BFC supplies the final consent wording (scope §11 item 13); the tick is required before payment */
    function consentBlock(text) {
      return '<label class="bfc-pay__consent bfc-q-anim"><input type="checkbox" data-consent><span>' + text + '</span></label>';
    }
    function payActions(kind, extra) {
      return '<div class="bfc-q__actions bfc-pay__actions bfc-q-anim">' +
        '<button type="button" class="bfc-btn bfc-btn--lg" data-pay="' + kind + '" disabled aria-describedby="bfc-pay-hint">Continue to payment</button>' +
        extra + '</div>' +
        '<p class="bfc-q__gate bfc-pay__hint" id="bfc-pay-hint" data-pay-hint>Tick the box above to continue.</p>' +
        '<p class="bfc-pay__status" data-pay-status role="status" hidden></p>';
    }

    /* the results screen's "Have a code?" box: only a listed code is accepted; it re-prices the screen */
    function wireCode() {
      var input = stage.querySelector('[data-code-input]');
      var apply = stage.querySelector('[data-code-apply]');
      var msg = stage.querySelector('[data-code-msg]');
      if (!input || !apply) return;
      function go() {
        var v = String(input.value || '').trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '');
        msg.classList.remove('is-ok');
        if (!v) { msg.textContent = 'Type the code first.'; return; }
        if (CODES.indexOf(v) < 0) { msg.textContent = 'We do not recognise ' + v + '. Check the spelling, or ask us.'; return; }
        if (window.BFCCampaign) window.BFCCampaign.set(v, 'manual');
        pushLayer({ event: 'bfc_code_applied', bfc_code: v });
        render();
      }
      apply.addEventListener('click', go);
      input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); go(); } });
    }

    function wireConfirm() {
      var box = stage.querySelector('[data-consent]');
      var pay = stage.querySelector('[data-pay]');
      var hint = stage.querySelector('[data-pay-hint]');
      var sw = stage.querySelector('[data-switch-plan]');
      if (sw) sw.addEventListener('click', function () { screen = 'confirm-plan'; render(); });
      if (!box || !pay) return;
      box.addEventListener('change', function () { pay.disabled = !box.checked; hint.hidden = box.checked; });
      pay.addEventListener('click', function () {
        var f = planFacts();
        if (!f || !box.checked) return;
        var detail = { choice: pay.getAttribute('data-pay'), answers: a, plan: f.r, shownPence: f.pence, code: f.code.code,
          consentText: box.parentNode.textContent.trim(),
          engineInput: engineInput(), ageMonths: ageMonths(), vetDiet: a.vet, dogName: (a.dogName || '').trim(),
          statusEl: stage.querySelector('[data-pay-status]'), button: pay,
          firstDelivery: f.firstDelivery, firstFullOrder: f.firstFullOrder, cancelBy: f.cancelBy,
          campaign: window.BFCCampaign ? window.BFCCampaign.info() : null };
        pushLayer({ event: 'bfc_begin_checkout', bfc_choice: detail.choice, bfc_price: (detail.choice === 'trial' ? TRIAL_PENCE : f.pence) / 100, bfc_code: f.code.code || '' });
        /* Milestone 3 hangs Recharge checkout off this event and calls preventDefault() once it has taken over */
        var ev = new CustomEvent('bfc:checkout', { bubbles: true, cancelable: true, detail: detail });
        if (!root.dispatchEvent(ev)) return;
        var st = stage.querySelector('[data-pay-status]');
        st.hidden = false;
        st.innerHTML = '<strong>Payment is not connected on this preview yet.</strong> This button opens the secure subscription checkout once Recharge is set up on the store.';
      });
    }

    function viewResult() {
      var stop = gateScreen();
      if (stop) { screen = stop; return stop === 'stop-age' ? viewStopAge() : viewStopVet(); }
      var f = planFacts({ offer: true });
      if (!f) return viewUnavailable();
      var r = f.r, name = esc(dog());
      var recipeName = f.recipeName;
      var bagLine = f.bagLine;
      var c = f.code;
      /* from exact consumption; a cycle over 20kg shows its later parcel on the day it is needed */
      var drops = engine().deliveries(r, { start: f.firstDelivery, count: 3 });
      var split = r.parcels.length > 1;
      pushLayer({ event: 'bfc_plan_result', bfc_recipe: recipeKey(), bfc_cycle_days: r.cycleDaysDisplay, bfc_price: f.pence / 100, bfc_code: c.code || '' });

      return '<p class="bfc-q__note bfc-q-anim">' + name + '’s plan</p>' +
        '<h2 class="bfc-q__legend bfc-q-anim">' + g5(r.gramsPerDay) + 'g of ' + recipeName.toLowerCase() + ' a day</h2>' +
        '<div class="bfc-res__tiles bfc-q-anim">' +
          tile(g5(r.gramsPerDay) + 'g', 'every day') +
          tile(money(Math.round(f.pence / r.cycleDays)), 'a day') +
          tile(money(Math.round(f.pence / r.cycleDays * engine().config.daysPerMonth)), 'a month, near enough') +
        '</div>' +
        /* Revision 2 item 27 */
        '<p class="bfc-res__basis bfc-q-anim">Based on ' + name + '’s age, weight, activity, neuter status and weight goal.</p>' +
        '<div class="bfc-res__cycle bfc-q-anim">' +
          /* Revision 2 item 13 */
          '<p class="bfc-res__cycleline">Your delivery cycle: <strong>' + f.cycle + '</strong>.</p>' +
          bagStack(recipeKey(), r.bags) +
          '<p class="bfc-res__bags">' + bagLine + ' of ' + recipeName +
            (split ? ' · too much for one parcel, so it comes as ' + r.parcels.length + ' parcels, each one when ' + name + ' needs it' : '') +
          '</p>' +
          '<ol class="bfc-res__dates">' + drops.map(function (d, i) {
            var label = i === 0 ? 'First delivery' : (d.charged ? 'Then' : 'Next parcel');
            return '<li><span>' + label + (split ? ' · ' + d.kg + 'kg' : '') + '</span><strong>' + fmt(d.date) + '</strong></li>'; }).join('') + '</ol>' +
          '<p class="bfc-res__flex">Cancel, delay a delivery or bring the next one forward at any time, without penalty.</p>' +
        '</div>' +
        /* Revision 2 items 28 + 30: everything a customer needs, together, before either button */
        '<dl class="bfc-res__facts bfc-q-anim">' +
          '<div><dt>Daily portion</dt><dd>' + g5(r.gramsPerDay) + 'g</dd></div>' +
          '<div><dt>Delivery quantity</dt><dd>' + r.bagsKg + 'kg</dd></div>' +
          '<div><dt>Delivery cycle</dt><dd>' + cap(f.cycle) + '</dd></div>' +
          '<div><dt>Price per delivery</dt><dd>' + money(f.pence) + '</dd></div>' +
          '<div><dt>First delivery</dt><dd>' + fmt(f.firstDelivery) + '</dd></div>' +
        '</dl>' +
        /* Revision 2 items 29 + 30: two separate cards, the full plan first (and first on phones) */
        '<div class="bfc-res__choice bfc-q-anim">' +
          '<div class="bfc-res__card bfc-res__card--plan">' +
            '<h3>Start the full plan</h3>' +
            '<dl class="bfc-res__summary">' +
              '<div><dt>Each delivery</dt><dd>' + (bagLine || r.bagsKg + 'kg') + '</dd></div>' +
              '<div><dt>Price</dt><dd>' + money(f.pence) + ' ' + f.cycle + '</dd></div>' +
              '<div><dt>First payment</dt><dd>Today</dd></div>' +
              '<div><dt>First delivery</dt><dd>' + fmt(f.firstDelivery) + '</dd></div>' +
            '</dl>' +
            codeBox(c) +
            '<button type="button" class="bfc-btn bfc-btn--lg" data-choose="subscribe">Start the full plan</button>' +
          '</div>' +
          (!OFFER_TRIAL ? '' : '<div class="bfc-res__card bfc-res__card--trial">' +
            '<h3>Try ' + TRIAL_KG + 'kg for ' + money(TRIAL_PENCE) + ' first</h3>' +
            '<p>Not ready for the full bag? Start with ' + TRIAL_KG + 'kg for ' + money(TRIAL_PENCE) + ' including VAT and delivery - 50% off!</p>' +
            '<dl class="bfc-res__summary">' +
              '<div><dt>Today</dt><dd>' + TRIAL_KG + 'kg trial, ' + money(TRIAL_PENCE) + '</dd></div>' +
              '<div><dt>Then</dt><dd>' + money(f.pence) + ' ' + f.cycle + ', first charged ' + fmt(f.firstFullOrder) + '</dd></div>' +
              '<div><dt>Cancel before</dt><dd>' + fmt(f.cancelBy) + ' to stop the first full order</dd></div>' +
            '</dl>' +
            '<button type="button" class="bfc-btn bfc-btn--lg bfc-btn--outline" data-choose="trial">Try ' + TRIAL_KG + 'kg for ' + money(TRIAL_PENCE) + ' first</button>' +
          '</div>') +
        '</div>' +
        saveBox() +
        '<p class="bfc-res__reassure bfc-q-anim">Change recipe, pause, skip, reschedule or cancel online.</p>' +
        '<p class="bfc-res__legal bfc-q-anim">VAT and UK delivery included. The trial is the first delivery of ' +
          name + '’s plan, not a separate purchase: we send a reminder before the first full order, and unless you ' +
          'cancel before the date above it goes ahead automatically. We work the portion out from what you told us; ' +
          'tell us if ' + name + ' changes and we will work it out again.</p>' +
        '<div class="bfc-q__actions bfc-q-anim"><button type="button" class="bfc-q__back2" data-edit="review">Change an answer</button></div>';
    }

    /* Defaults 6 + Revision 3 item 3: the promo code field sits inside the full-plan card. A linked or typed code shows
       as applied; with none, "Start the full plan" offers WEB10 in a pop-up (wireOffer). */
    function codeBox(c) {
      var applied = c.code && c.source !== 'default';
      return '<div class="bfc-res__code">' +
          '<label class="bfc-res__codelabel" for="bfc-code-input">Promo code</label>' +
          '<div class="bfc-res__coderow">' +
          '<input id="bfc-code-input" type="text" autocomplete="off" autocapitalize="characters" spellcheck="false" maxlength="40" placeholder="EXAMPLE10" data-code-input' +
            (applied ? ' value="' + esc(c.code) + '"' : '') + '>' +
          '<button type="button" class="bfc-btn bfc-btn--outline" data-code-apply>Apply</button></div>' +
          '<p class="bfc-res__codemsg' + (applied ? ' is-ok' : '') + '" data-code-msg role="status">' +
            (applied ? '<strong>' + esc(c.code) + '</strong> applied: ' + c.pct + '% off every delivery, for life.' : '') + '</p>' +
        '</div>';
    }
    /* 2026-10-20 (Dom): "Send me <dog>'s plan". The service (POST /plan/save) re-runs the engine, keeps the lead,
       adds the customer in Shopify and emails the plan with a link back to this screen. Without the service the
       box is left out, rather than offering an email nobody sends. */
    var SERVICE = (root.getAttribute('data-service') || '').replace(/\/+$/, '');
    var savedTo = '';          /* the address the plan went to, this visit */
    var savedSent = false;
    function saveBox() {
      if (!SERVICE) return '';
      var name = esc(dog());
      if (savedTo) {
        return '<div class="bfc-res__save is-done bfc-q-anim" role="status"><p><strong>' + (savedSent ? 'Sent.' : 'Saved.') + '</strong> ' +
          name + '’s plan is on its way to ' + esc(savedTo) + '. If it is not there in a few minutes, check your spam folder.</p></div>';
      }
      return '<div class="bfc-res__save bfc-q-anim">' +
          '<h3>Send me ' + name + '’s plan</h3>' +
          '<p class="bfc-res__savelede">Not ready yet? We will email you the plan, with a link straight back to it.</p>' +
          '<div class="bfc-q__fields bfc-q__fields--pair">' +
            field('firstName', 'Your first name', 'text', a.firstName, 'given-name') +
            field('email', 'Your email', 'email', a.email, 'email') +
          '</div>' +
          '<label class="bfc-q__consent"><input type="checkbox" data-field="marketing"' + (a.marketing ? ' checked' : '') +
            '><span>Also email me feeding tips, rescue stories and the occasional offer. You can stop at any time.</span></label>' +
          '<button type="button" class="bfc-btn bfc-btn--lg bfc-btn--outline" data-save-plan>Send me ' + name + '’s plan</button>' +
          '<p class="bfc-q__gate bfc-res__savemsg" data-save-msg role="alert"></p>' +
        '</div>';
    }

    /* The answers that rebuild this screen, for the link in the email. Nothing personal: no name, no email. */
    var RESTORE_KEYS = ['dogName', 'breedType', 'breeds', 'ageYears', 'ageMonths', 'weight', 'weightUnit', 'neutered',
      'activity', 'goal', 'vet', 'vetType', 'recipe'];
    function restoreToken() {
      var o = {};
      RESTORE_KEYS.forEach(function (k) { if (a[k] != null && a[k] !== '') o[k] = a[k]; });
      var b = btoa(unescape(encodeURIComponent(JSON.stringify(o))));
      return b.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }

    function wireSave() {
      var btn = stage.querySelector('[data-save-plan]');
      var msg = stage.querySelector('[data-save-msg]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        var first = String(a.firstName || '').trim(), email = String(a.email || '').trim();
        if (!first) { msg.textContent = 'Add your first name.'; return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { msg.textContent = 'Check your email address.'; return; }
        msg.textContent = '';
        btn.disabled = true; btn.classList.add('is-busy');
        fetch(SERVICE + '/plan/save', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ firstName: first, email: email, marketing: !!a.marketing, dogName: (a.dogName || '').trim(),
            answers: Object.assign({}, engineInput(), { ageMonths: ageMonths(), vetDiet: a.vet }), restore: restoreToken(),
            campaign: window.BFCCampaign ? window.BFCCampaign.info() : null }) })
          .then(function (res) {
            return res.json().catch(function () { return {}; }).then(function (d) {
              if (!res.ok || d.ok === false) throw new Error(d.message || 'Something went wrong. Please try again.');
              return d;
            });
          })
          .then(function (d) {
            savedTo = email; savedSent = !!d.sent;
            pushLayer({ event: 'bfc_plan_emailed', bfc_marketing: !!a.marketing });
            var box = stage.querySelector('.bfc-res__save');
            if (box) {
              box.outerHTML = saveBox();
              var done = stage.querySelector('.bfc-res__save');
              if (done) done.classList.add('is-in');
            }
          })
          .catch(function (err) {
            btn.disabled = false; btn.classList.remove('is-busy');
            msg.textContent = err.message;
          });
      });
    }
    /* back from the email link: ?plan=<token> brings the answers back and opens the results */
    function restoreFromLink() {
      var m = /[?&]plan=([A-Za-z0-9_-]+)/.exec(location.search);
      if (!m) return false;
      var got = null;
      try {
        var b = m[1].replace(/-/g, '+').replace(/_/g, '/');
        got = JSON.parse(decodeURIComponent(escape(atob(b + '==='.slice((b.length + 3) % 4)))));
      } catch (e) { got = null; }
      try {
        var u = new URL(location.href); u.searchParams.delete('plan');
        history.replaceState(null, '', u.pathname + u.search + u.hash);
      } catch (e) {}
      if (!got || typeof got !== 'object') return false;
      var keep = { firstName: a.firstName, email: a.email, marketing: a.marketing };
      a = {};
      RESTORE_KEYS.forEach(function (k) { if (got[k] != null) a[k] = got[k]; });
      Object.keys(keep).forEach(function (k) { if (keep[k] != null) a[k] = keep[k]; });
      a = sanitize(a);
      save();
      return true;
    }

    function cap(t) { return t.charAt(0).toUpperCase() + t.slice(1); }
    /* Revision 2 item 48: plan-funnel events for Google Tag Manager (Meta tags live in the container, gated by consent).
       GA4 loads directly (snippets/bfc-gtm-head sets window.bfcGA4), so each event is also sent to it by the same name. */
    function pushLayer(o) {
      try {
        window.dataLayer = window.dataLayer || []; window.dataLayer.push(o);
        if (window.bfcGA4 && typeof window.gtag === 'function') {
          var p = { currency: 'GBP' }, k;
          for (k in o) if (k !== 'event') p[k] = o[k];
          window.gtag('event', o.event, p);
        }
      } catch (e) {}
    }

    /* the actual bags we would send, at true relative size — 10kg reads bigger than 2kg */
    function bagStack(recipe, b) {
      var src = BAGS_SRC[recipe] || {}, out = '', i;
      for (i = 0; i < (b[10] || 0); i++) if (src[10]) out += '<img class="bfc-res__bag bfc-res__bag--10" src="' + src[10] + '" alt="" loading="lazy">';
      for (i = 0; i < (b[2] || 0); i++) if (src[2]) out += '<img class="bfc-res__bag bfc-res__bag--2" src="' + src[2] + '" alt="" loading="lazy">';
      return out ? '<div class="bfc-res__stack" aria-hidden="true">' + out + '</div>' : '';
    }

    function tile(big, small) {
      return '<div class="bfc-res__tile"><strong>' + big + '</strong><span>' + small + '</span></div>';
    }

    /* ---- events ---- */
    function wire() {
      stage.querySelectorAll('[data-choice]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var k = btn.getAttribute('data-choice');
          a[k] = btn.getAttribute('data-value');
          if (k === 'breedType') a.breeds = [];
          if (k === 'vet' && a.vet !== 'yes') a.vetType = '';
          save(); render(k + '|' + btn.getAttribute('data-value'));
        });
      });
      stage.querySelectorAll('[data-field]').forEach(function (el) {
        var ev = el.type === 'checkbox' ? 'change' : 'input';
        el.addEventListener(ev, function () {
          a[el.getAttribute('data-field')] = el.type === 'checkbox' ? el.checked : el.value;
          save();
          var next = stage.querySelector('[data-next]');
          if (next) next.disabled = !valid(STEPS[idx].id);
        });
      });
      stage.querySelectorAll('[data-edit]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var id = btn.getAttribute('data-edit');
          screen = 'step';
          if (id === 'review') { idx = 9; editing = false; }
          else { idx = STEPS.map(function (s) { return s.id; }).indexOf(id); editing = true; }
          render();
        });
      });
      var next = stage.querySelector('[data-next]');
      if (next) next.addEventListener('click', advance);
      var wl = stage.querySelector('[data-waitlist-done]');
      if (wl) wl.addEventListener('click', joinWaitlist);
      stage.querySelectorAll('[data-choose]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var choice = btn.getAttribute('data-choose');
          if (choice === 'subscribe' && needsOffer(btn)) return;
          root.dispatchEvent(new CustomEvent('bfc:plan-choice', { bubbles: true, detail: { choice: choice, answers: a } }));
          screen = choice === 'trial' ? 'confirm-trial' : 'confirm-plan';
          render();
        });
      });
      wireConfirm();
      wireCode();
      wireSave();
      breedSearch();
      arrowKeys();
    }

    /* Revision 3 item 3: "Start the full plan" with no promo code entered opens the WEB10 offer. A code typed but not yet
       applied is applied first (a wrong one stops here with the message under the field). */
    function needsOffer(btn) {
      var input = stage.querySelector('[data-code-input]');
      var typed = input ? String(input.value || '').trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '') : '';
      var c = codeState();
      if (typed && typed !== (c.source !== 'default' ? c.code : '')) {
        if (CODES.indexOf(typed) < 0) { stage.querySelector('[data-code-apply]').click(); return true; }
        if (window.BFCCampaign) window.BFCCampaign.set(typed, 'manual');
        pushLayer({ event: 'bfc_code_applied', bfc_code: typed });
        return false;
      }
      if (c.code && c.source !== 'default') return false;
      if (!DEFAULT_CODE || CODES.indexOf(DEFAULT_CODE) < 0) return false;
      openOffer(btn);
      return true;
    }

    function openOffer(trigger) {
      var pct = engine() ? engine().config.pricing.attributionDiscountPercent : 10;
      var dlg = document.createElement('dialog');
      dlg.className = 'bfc-offer';
      dlg.setAttribute('aria-labelledby', 'bfc-offer-text');
      dlg.innerHTML = '<form method="dialog" class="bfc-offer__box">' +
          '<button type="submit" class="bfc-offer__x" value="close" aria-label="Close"><svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
          '<p class="bfc-offer__badge" aria-hidden="true">' + pct + '%</p>' +
          '<p class="bfc-offer__text" id="bfc-offer-text">We’d like to offer you a lifetime ' + pct + '% discount, use <strong>' + esc(DEFAULT_CODE) + '</strong></p>' +
          '<button type="submit" class="bfc-btn bfc-btn--lg bfc-offer__use" value="use">Use ' + esc(DEFAULT_CODE) + '</button>' +
        '</form>';
      document.body.appendChild(dlg);
      dlg.addEventListener('click', function (e) { if (e.target === dlg) dlg.close('close'); });
      dlg.addEventListener('close', function () {
        var used = dlg.returnValue === 'use';
        dlg.remove();
        if (used) {
          if (window.BFCCampaign) window.BFCCampaign.set(DEFAULT_CODE, 'manual');
          pushLayer({ event: 'bfc_code_applied', bfc_code: DEFAULT_CODE });
          root.dispatchEvent(new CustomEvent('bfc:plan-choice', { bubbles: true, detail: { choice: 'subscribe', answers: a } }));
          screen = 'confirm-plan';
          render();
        } else if (trigger && document.contains(trigger)) trigger.focus();
      });
      if (dlg.showModal) dlg.showModal(); else dlg.setAttribute('open', '');
      var use = dlg.querySelector('.bfc-offer__use'); if (use) use.focus();
    }

    function breedSearch() {
      var input = stage.querySelector('[data-breed-input]');
      var list = stage.querySelector('[data-breed-list]');
      if (!input || !list) { unpick(); return; }
      var active = -1, shown = [];
      function close() { list.hidden = true; list.innerHTML = ''; active = -1; shown = []; }
      function draw() {
        var q = input.value.trim().toLowerCase();
        if (q.length < 2) return close();
        var chosen = a.breeds || [];
        shown = (engine() ? engine().breedNames() : []).filter(function (b) {
          return chosen.indexOf(b) < 0 && b.toLowerCase().indexOf(q) >= 0;
        }).sort(function (x, y) {
          return (x.toLowerCase().indexOf(q) - y.toLowerCase().indexOf(q)) || x.localeCompare(y);
        }).slice(0, 8);
        if (!shown.length) { list.hidden = false; list.innerHTML = '<li class="bfc-q__none">No breed by that name. Pick “A mix” or “Not sure”.</li>'; return; }
        list.hidden = false;
        list.innerHTML = shown.map(function (b, i) {
          return '<li><button type="button" data-pick="' + esc(b) + '"' + (i === active ? ' class="is-on"' : '') + '>' + esc(b) + '</button></li>'; }).join('');
        list.querySelectorAll('[data-pick]').forEach(function (btn) {
          btn.addEventListener('mousedown', function (e) { e.preventDefault(); pick(btn.getAttribute('data-pick')); });
        });
      }
      function pick(name) {
        a.breeds = (a.breeds || []).concat([name]);
        save(); render();
        var inp = stage.querySelector('[data-breed-input]');
        if (inp) inp.focus();
      }
      input.addEventListener('input', draw);
      input.addEventListener('blur', function () { setTimeout(close, 120); });
      input.addEventListener('keydown', function (e) {
        if (list.hidden || !shown.length) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          active = (active + (e.key === 'ArrowDown' ? 1 : shown.length - 1)) % shown.length;
          draw();
        } else if (e.key === 'Enter') { e.preventDefault(); pick(shown[active < 0 ? 0 : active]); }
        else if (e.key === 'Escape') close();
      });
      unpick();
      function unpick() {
        stage.querySelectorAll('[data-unpick]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var n = btn.getAttribute('data-unpick');
            a.breeds = (a.breeds || []).filter(function (x) { return x !== n; });
            save(); render();
          });
        });
      }
    }

    /* The age rule and the vet-diet stop come from the engine config. Each step only checks its own rule. */
    function eligibilityNow(id) {
      var months = ageMonths();
      if (!engine()) return (id === 'age' && months < 12) ? 'puppy' : (id === 'vet' && a.vet === 'yes' ? 'vet_diet' : null);
      var r = engine().eligibility({ ageMonths: id === 'age' ? months : Infinity, vetDiet: id === 'vet' ? a.vet : 'no' });
      return r.reason;
    }

    /* ---- waitlist: a real Shopify customer signup, tagged, sent only when the box is ticked ----
       POST /plan/waitlist on the service creates or updates the customer with marketing consent and the list tags
       (Logic v3: record diet type and permission). 2026-10-22: it used to post a hidden Shopify customer form from
       script, which Shopify's CAPTCHA refused ("Missing CAPTCHA token"), so nothing was ever saved. */
    var WAITLIST_STATUS = '';

    /* 2026-10-20: the email is no longer asked at step 1, so the stop screens ask for it themselves */
    function waitlistBox(text) {
      if (WAITLIST_STATUS === 'ok') return '';
      return '<div class="bfc-q__fields bfc-q-anim">' + field('email', 'Your email', 'email', a.email, 'email') + '</div>' +
        '<label class="bfc-q__consent bfc-q-anim"><input type="checkbox" data-field="waitlist"' + (a.waitlist ? ' checked' : '') +
        '><span>' + text + '</span></label>';
    }
    function waitlistAction() {
      if (WAITLIST_STATUS === 'ok') {
        return '<p class="bfc-q__aside bfc-q-anim" role="status"><strong>You are on the list.</strong> We will email ' + esc(a.email || 'you') + ' when there is news.</p>';
      }
      return '<button type="button" class="bfc-btn bfc-btn--lg" data-waitlist-done>Keep me posted</button>' +
        '<span class="bfc-q__gate" data-waitlist-hint role="alert" hidden>Tick the box first, so we know you want the email.</span>';
    }
    function joinWaitlist() {
      var btn = stage.querySelector('[data-waitlist-done]');
      var hint = stage.querySelector('[data-waitlist-hint]');
      var say = function (text) { if (hint) { hint.textContent = text; hint.hidden = false; } };
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(a.email || '').trim())) { say('Add your email address first.'); return; }
      if (!a.waitlist) { say('Tick the box first, so we know you want the email.'); return; }
      if (hint) hint.hidden = true;
      btn.disabled = true; btn.classList.add('is-busy');
      var list = screen === 'stop-age' ? 'puppy' : 'vet-diet';
      fetch(SERVICE + '/plan/waitlist', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email: String(a.email).trim(), list: list, consent: true, vetType: list === 'vet-diet' ? (a.vetType || '') : '' }) })
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (d) {
            if (!res.ok || d.ok === false) throw new Error(d.message || 'That did not go through. Check your email address and try again.');
          });
        })
        .then(function () {
          WAITLIST_STATUS = 'ok';
          pushLayer({ event: 'bfc_waitlist_joined', bfc_list: list });
          render();
        })
        .catch(function (err) {
          btn.disabled = false; btn.classList.remove('is-busy');
          say(err.message);
        });
    }

    function advance() {
      var id = STEPS[idx].id;
      if (!valid(id)) return;
      if (id === 'age' || id === 'vet') {
        var gate = eligibilityNow(id);
        if (gate === 'puppy') { screen = 'stop-age'; render(); return; }
        if (gate === 'vet_diet') { screen = 'stop-vet'; render(); return; }
      }
      if (editing) { editing = false; idx = 9; render(); return; }
      if (id === 'review') { screen = 'result'; render(); return; }
      idx = Math.min(idx + 1, STEPS.length - 1);
      render();
    }

    if (backBtn) backBtn.addEventListener('click', function () {
      if (screen.indexOf('confirm') === 0) { screen = 'result'; render(); return; }
      if (screen !== 'step') { screen = 'step'; render(); return; }
      if (idx > 0) { idx--; editing = false; render(); }
    });

    var restored = restoreFromLink();
    /* a saved run comes back to the first unanswered step, never mid-air */
    for (var i = 0; i < STEPS.length; i++) { if (!valid(STEPS[i].id)) { idx = i; break; } idx = i; }
    screen = gateScreen() || (restored && idx === STEPS.length - 1 ? 'result' : screen);
    render();
  }

  function boot() { document.querySelectorAll('[data-bfc-plan]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
